// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable max-lines -- Single hook managing the entire multi-step guided upload workflow (state, navigation, file preview, upload, date validation). Splitting would fragment tightly coupled step logic. */
import { isDefined } from "@clipboard-health/util-ts";
import { useToast } from "@src/appV2/lib";
import { logError, logEvent } from "@src/appV2/lib/analytics";
import { APP_V2_APP_EVENTS, APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics/events";
import { invalidateWorkerRequirements } from "@src/appV2/redesign/Documents/api/invalidateWorkerRequirements";
import { useDocumentUploadFlow } from "@src/appV2/redesign/Documents/Details/Upload/useDocumentUploadFlow";
import { useRequirementDetailsContext } from "@src/appV2/redesign/Documents/Details/useRequirementDetailsContext";
import { DOCUMENT_DETAILS_FULL_PATH } from "@src/appV2/redesign/Documents/paths";
import {
  type UploadedFile,
  useFileUploaderContext,
} from "@src/appV2/redesign/Documents/useFileUploaderContext";
import { useQueryClient } from "@tanstack/react-query";
import { isAfter, startOfDay } from "date-fns";
import { useCallback, useState } from "react";
import { generatePath, useHistory } from "react-router-dom";

import {
  generateScheduleTbTestPath,
  getMinimumTestDate,
  SCHEDULE_TB_TEST_SOURCE,
  TB_TEST_GUIDED_UPLOAD_ANALYTICS_FLOW,
  TB_TEST_GUIDED_UPLOAD_TERMINAL_STEPS,
  TB_TEST_NOT_COMPLETED_RECENTLY_OPTION,
  type TestTypeSelectionOption,
} from "../constants";
import { TB_TEST_TYPE, type TbTestGuidedUploadStep, type TbTestType } from "../types";

export function useTbTestGuidedUploadWorkflow() {
  const { requirement, setUploadedDocument } = useRequirementDetailsContext();
  const { uploadedFiles, removeFile, replaceFile, clearFiles } = useFileUploaderContext();
  const { mutateAsync: uploadDocument, isLoading: isUploading } = useDocumentUploadFlow();
  const { showErrorToast } = useToast();
  const queryClient = useQueryClient();
  const history = useHistory();

  const { id: requirementId = "", attributes } = requirement.requirement ?? {};
  const { name: requirementName = "" } = attributes ?? {};

  const [step, setStep] = useState<TbTestGuidedUploadStep>("test-type-selection");
  const [selectedOption, setSelectedOption] = useState<TestTypeSelectionOption | undefined>(
    undefined
  );
  const [testType, setTestType] = useState<TbTestType | undefined>(undefined);
  const [selectedTbTestDate, setSelectedTbTestDate] = useState<Date | undefined>(undefined);
  const [previewFileId, setPreviewFileId] = useState<string | undefined>(undefined);

  const previewFile = uploadedFiles.find((file) => file.id === previewFileId);

  const reset = useCallback(() => {
    setStep("test-type-selection");
    setSelectedOption(undefined);
    setTestType(undefined);
    setSelectedTbTestDate(undefined);
    setPreviewFileId(undefined);
    clearFiles();
  }, [clearFiles]);

  const navigateToDetails = useCallback(() => {
    clearFiles();
    history.replace(generatePath(DOCUMENT_DETAILS_FULL_PATH, { requirementId }));
  }, [clearFiles, history, requirementId]);

  const handleNext = useCallback(() => {
    switch (step) {
      case "test-type-selection": {
        // Handled by handleTestTypeSelectionConfirmed
        break;
      }
      case "instructions": {
        setStep("upload");
        break;
      }
      case "upload": {
        setStep("date-validation");
        break;
      }
      case "file-preview": {
        setPreviewFileId(undefined);
        setStep("upload");
        break;
      }
      case "date-validation": {
        // Handled by handleTbTestDateSubmitted
        break;
      }
      case "invalid-test-error": {
        reset();
        break;
      }
      case "success": {
        logEvent(APP_V2_USER_EVENTS.TB_TEST_GUIDED_UPLOAD_COMPLETED, {
          requirementId,
          testType,
        });
        navigateToDetails();
        break;
      }
      default:
        step satisfies never; // Exhaustive check to ensure all steps are handled
        break;
    }
  }, [step, testType, requirementId, navigateToDetails, reset]);

  const handleBack = useCallback(() => {
    switch (step) {
      case "test-type-selection": {
        logEvent(APP_V2_USER_EVENTS.TB_TEST_GUIDED_UPLOAD_ABANDONED, {
          requirementId,
          abandonedAtStep: step,
          testType,
        });
        navigateToDetails();
        break;
      }
      case "instructions": {
        setStep("test-type-selection");
        break;
      }
      case "upload": {
        setStep("instructions");
        break;
      }
      case "file-preview": {
        setPreviewFileId(undefined);
        setStep("upload");
        break;
      }
      case "date-validation": {
        setStep("upload");
        break;
      }
      case "invalid-test-error": {
        setSelectedTbTestDate(undefined);
        setStep("date-validation");
        break;
      }
      case "success": {
        navigateToDetails();
        break;
      }
      default:
        step satisfies never;
        break;
    }
  }, [step, testType, requirementId, navigateToDetails]);

  const handleTestTypeSelectionConfirmed = useCallback(
    (confirmed: TestTypeSelectionOption) => {
      switch (confirmed) {
        case TB_TEST_TYPE.TUBERCULIN_SKIN_TEST:
        case TB_TEST_TYPE.TB_BLOOD_TEST:
        case TB_TEST_TYPE.CHEST_X_RAY: {
          setTestType(confirmed);
          logEvent(APP_V2_USER_EVENTS.TB_TEST_GUIDED_UPLOAD_TEST_TYPE_SELECTED, {
            requirementId,
            testType: confirmed,
          });
          setStep("instructions");
          break;
        }
        case TB_TEST_NOT_COMPLETED_RECENTLY_OPTION: {
          logEvent(APP_V2_USER_EVENTS.TB_TEST_GUIDED_UPLOAD_NOT_COMPLETED_RECENTLY_SELECTED, {
            requirementId,
          });
          history.push(
            generateScheduleTbTestPath(
              requirementId,
              SCHEDULE_TB_TEST_SOURCE.NOT_COMPLETED_RECENTLY
            )
          );
          break;
        }
        default:
          confirmed satisfies never; // Exhaustive check to ensure all options are handled
          break;
      }
    },
    [requirementId, history]
  );

  const handlePreviewFile = useCallback(
    (fileId: string) => {
      const file = uploadedFiles.find((f) => f.id === fileId);
      if (!isDefined(file)) {
        return;
      }

      setPreviewFileId(fileId);
      setStep("file-preview");

      logEvent(APP_V2_USER_EVENTS.TB_TEST_GUIDED_UPLOAD_UPLOADED_FILE_PREVIEWED, {
        requirementId,
        testType,
        fileContentType: file.contentType,
      });
    },
    [uploadedFiles, requirementId, testType]
  );

  const handleReplaceFile = useCallback(
    (newFile: UploadedFile) => {
      if (!isDefined(previewFileId)) {
        return;
      }

      replaceFile(previewFileId, newFile);
      setPreviewFileId(newFile.id);

      logEvent(APP_V2_USER_EVENTS.TB_TEST_GUIDED_UPLOAD_PREVIEWED_FILE_REPLACED, {
        requirementId,
        testType,
        newFileContentType: newFile.contentType,
      });
    },
    [previewFileId, replaceFile, requirementId, testType]
  );

  const handleDeletePreviewedFile = useCallback(() => {
    if (!isDefined(previewFileId)) {
      return;
    }

    removeFile(previewFileId);
    setPreviewFileId(undefined);
    setStep("upload");

    logEvent(APP_V2_USER_EVENTS.TB_TEST_GUIDED_UPLOAD_PREVIEWED_FILE_REMOVED, {
      requirementId,
      testType,
    });
  }, [previewFileId, removeFile, requirementId, testType]);

  const handleDateChange = useCallback((date: Date) => {
    setSelectedTbTestDate(date);
  }, []);

  const handleTbTestDateSubmitted = useCallback(async () => {
    // These should never be undefined, but we'll check just to be safe
    if (!isDefined(selectedTbTestDate) || !isDefined(testType)) {
      return;
    }

    const minimumTestDate = startOfDay(getMinimumTestDate(testType));
    // !isAfter rejects the exact boundary date — the test date must be strictly after the minimum.
    if (!isAfter(startOfDay(selectedTbTestDate), minimumTestDate)) {
      logEvent(APP_V2_USER_EVENTS.TB_TEST_GUIDED_UPLOAD_INVALID_TEST_DATE, {
        requirementId,
        testType,
        selectedDate: selectedTbTestDate.toISOString(),
      });
      setStep("invalid-test-error");
      return;
    }

    try {
      const { data } = await uploadDocument({
        requirementName,
        requirementId,
        files: uploadedFiles,
      });

      setUploadedDocument(data);
      void invalidateWorkerRequirements(queryClient);
      clearFiles();

      logEvent(APP_V2_USER_EVENTS.TB_TEST_GUIDED_UPLOAD_DOCUMENT_UPLOADED, {
        requirementId,
        testType,
      });

      // Emit standard upload event to match normal document upload flow (see PreviewPage.tsx)
      logEvent(APP_V2_USER_EVENTS.UPLOAD_DOCUMENT_SUCCESS, {
        requirement_id: requirementId,
        flow: TB_TEST_GUIDED_UPLOAD_ANALYTICS_FLOW,
      });

      setStep("success");
    } catch (error) {
      logError(APP_V2_APP_EVENTS.TB_TEST_GUIDED_UPLOAD_UPLOAD_FAILED, {
        error,
        metadata: {
          requirementId,
          testType,
        },
      });

      // Emit standard upload event to match normal document upload flow (see PreviewPage.tsx)
      logEvent(APP_V2_USER_EVENTS.UPLOAD_DOCUMENT_FAILURE, {
        requirement_id: requirementId,
        flow: TB_TEST_GUIDED_UPLOAD_ANALYTICS_FLOW,
      });
      showErrorToast("Failed to upload document. Please try again.");
    }
  }, [
    selectedTbTestDate,
    requirementId,
    requirementName,
    testType,
    uploadedFiles,
    uploadDocument,
    setUploadedDocument,
    queryClient,
    clearFiles,
    showErrorToast,
  ]);

  const handleClose = useCallback(() => {
    if (!TB_TEST_GUIDED_UPLOAD_TERMINAL_STEPS.includes(step)) {
      logEvent(APP_V2_USER_EVENTS.TB_TEST_GUIDED_UPLOAD_ABANDONED, {
        requirementId,
        abandonedAtStep: step,
        testType,
      });
    }

    navigateToDetails();
  }, [step, testType, requirementId, navigateToDetails]);

  const hasFiles = uploadedFiles.length > 0;

  return {
    step,
    selectedOption,
    setSelectedOption,
    testType,
    selectedTbTestDate,
    isUploading,
    hasFiles,
    handleTestTypeSelectionConfirmed,
    previewFile,
    handleNext,
    handleBack,
    handleClose,
    handlePreviewFile,
    handleReplaceFile,
    handleDeletePreviewedFile,
    handleDateChange,
    handleTbTestDateSubmitted,
    reset,
  };
}
